/**
 * NanoID Regex Validator
 *
 * Centralized validation, generation, and type safety for chat nanoIds.
 * Format: XXXXX_XXXXX (5 uppercase alphanumeric + underscore + 5 uppercase alphanumeric)
 *
 * @example
 * import { NanoIdRegex } from '@nexus/types';
 *
 * // Validate
 * if (NanoIdRegex.test(input)) {
 *   const chatId = NanoIdRegex.parse(input);
 * }
 *
 * // Generate
 * const newNanoId = NanoIdRegex.generate();
 */

import { BaseRegexValidator } from "./base-regex-validator";

/**
 * Branded type for validated NanoIDs
 * Prevents mixing regular strings with validated NanoIDs
 */
export type NanoId = string & { readonly __brand: "NanoId" };

/**
 * NanoID regex validator implementation
 */
class NanoIdRegexValidator extends BaseRegexValidator<NanoId> {
  constructor() {
    super({
      pattern: /^[0-9A-Z]{5}_[0-9A-Z]{5}$/,
      description:
        "NanoID format: 5 uppercase alphanumeric chars + underscore + 5 uppercase alphanumeric chars",
      examples: ["AB12C_XY89Z", "00000_ZZZZZ", "A1B2C_D3E4F"] as const,
      errorMessage: "Invalid NanoID format. Expected format: XXXXX_XXXXX (e.g., AB12C_XY89Z)",
      generator: generateNanoId,
      transform: (value) => value as NanoId
    });
  }

  /**
   * Check if a value could be a partial NanoID match
   * Useful for search operations that support partial matches
   */
  couldBePartialMatch(value: string): boolean {
    // Allow partial matches for search
    // Must be uppercase alphanumeric and optionally include underscore
    const partialPattern = /^[0-9A-Z_]+$/;
    return partialPattern.test(value) && value.length <= 11;
  }
}

/**
 * Generate a valid NanoID
 * Format: XXXXX_XXXXX (11 chars total)
 */
function generateNanoId(): NanoId {
  const chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";

  const generateSegment = (length: number): string => {
    let result = "";
    for (let i = 0; i < length; i++) {
      result += chars[Math.floor(Math.random() * chars.length)];
    }
    return result;
  };

  return `${generateSegment(5)}_${generateSegment(5)}` as NanoId;
}

/**
 * Singleton instance - use this for all NanoID validation
 */
export const NanoIdRegex = new NanoIdRegexValidator();

/**
 * Re-export for convenience
 */
export { generateNanoId };
